import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Button, Chip, Container, Divider, Link, Spacer, Typography } from '@emburse/embark-core';
import GLDDataTable from '../../../../views/QuickSetup/components/GLDDataTable/GLDDataTable';
import UploadDownload from '../../../../views/QuickSetup/components/UploadDownload';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
import { generateRandomGUID } from '../../../../util/helpers';
import { defaultValidation } from '../../../../util/formValidation';
const ExpenseReportDimensions = forwardRef(({ data, loading, upload, download, alert }, ref) => {
    useTranslation();
    const [tableData, setTableData] = useState([]);
    useEffect(() => {
        if (data && data.length) {
            let updatedData = JSON.parse(JSON.stringify(data));
            updatedData = updatedData.map((item) => ({
                ...item,
                Rows: item.Rows.map((row) => ({ ...row, id: generateRandomGUID() }))
            }));
            setTableData(updatedData);
        }
        else {
            setTableData([]);
        }
    }, [data]);
    const rowValidation = (row) => {
        const nameInvalid = defaultValidation(row.Name);
        const name = nameInvalid.length && { field: 'Name', reason: nameInvalid };
        return [name];
    };
    useImperativeHandle(ref, () => ({
        getData() {
            return JSON.stringify(tableData);
        },
        isValid() {
            const mappedTable = tableData.map((item) => {
                const filteredValidation = item.Rows.map((row) => rowValidation(row).filter((cell) => !!cell));
                const invalid = filteredValidation.filter((valid) => valid.length > 0);
                return (invalid.length > 0 && {
                    table: item.Label,
                    cell: filteredValidation.flat()
                });
            });
            return mappedTable.filter((item) => !!item);
        }
    }));
    const updateTableData = (rows, index) => {
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        tableDataCopy[index].Rows = rows;
        setTableData(tableDataCopy);
    };
    const updateLabel = (label, index) => {
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        tableDataCopy[index].Label = label;
        setTableData(tableDataCopy);
    };
    return (_jsxs(Container, { disableGutters: true, children: [_jsx(Typography, { variant: "h4", children: $t('Expense Report General Ledger Dimensions') }), _jsx(Spacer, { size: 10 }), _jsx(Chip, { label: $t('Optional Information'), variant: "outlined", color: "primary", size: "small" }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`Expense Report GLDs are additional attributes assigned to an expense. These GLDs add a field for 
                users to complete when they create an expense. Users must select a value from a prefilled search or drop-down menu.`) }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`For example, setting up an Expense Report GLD labeled 'Client' and adding values of Client A, Client B, 
                and Client C will allow visibility into which company a given expense is associated with.`) }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('Expense Report GLDs can also be used to set up custom expense report routing rules. More information can be found '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15460890503821-Additional-Locked-Approval-Workflow-Options", target: "_blank", children: $t('here') }), "."] }), _jsx(Spacer, { size: 35 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), alert.show && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 15 }), _jsx(Alert, { alertTitle: alert.title, severity: alert.severity, action: _jsx(Button, { color: alert.severity, onClick: alert.onClick, children: $t('See All Errors') }), children: alert.text })] })), _jsx(Spacer, { size: 35 }), _jsx(UploadDownload, { upload: upload, download: download }), _jsx(Spacer, { size: 15 }), tableData.map((GLD, index) => (_jsxs(Fragment, { children: [_jsx(GLDDataTable, { tableData: GLD.Rows, label: GLD.Label, loading: loading, index: index, updateTableData: (rows) => updateTableData(rows, index), updateLabel: (label) => updateLabel(label, index) }), _jsx(Spacer, { size: 35 })] }, GLD.id)))] }));
});
export default ExpenseReportDimensions;
